import { type Consumer, createConsumer } from '@rails/actioncable';

import { ANYCABLE_URL } from 'ev-config/config';

import { fetchAnycableToken } from './auth';
import {
  clearAllSubscriptions,
  setOnAnyDisconnected,
} from './subscriptionRegistry';

let cachedConsumer: Consumer | null = null;
let cachedToken: string | null = null;
let inflightPromise: Promise<Consumer | null> | null = null;

// Params retained from the first connect so the (short-lived) JWT can be
// re-fetched on reconnect — see refreshAnycableToken / onAnyDisconnected.
let cachedChannelName: string | null = null;
let cachedAuthToken: string | undefined;
let refreshPromise: Promise<void> | null = null;

/**
 * Create or return the cached Action Cable consumer with JWT auth (jid query param).
 * Reuses one connection for the page session; concurrent callers share the same
 * in-flight request to avoid duplicate connections.
 *
 * The connection URL is built from a function, not a fixed string, so Action Cable
 * re-evaluates it (and picks up a refreshed token) on every reconnect attempt. The
 * server-issued JWT is short-lived (5 min by default), so on any disconnect we
 * re-fetch the token before the connection monitor reconnects — otherwise the retry
 * would replay an expired token and the connection (and all subscriptions) would be
 * silently rejected. See `refreshAnycableToken` and the `disconnected` hook in
 * `subscriptionRegistry`.
 */
export async function getAnycableConsumer(
  connectionChannelName: string,
  authToken: string | undefined,
): Promise<Consumer | null> {
  if (cachedConsumer && cachedToken) {
    return cachedConsumer;
  }

  if (inflightPromise) {
    return inflightPromise;
  }

  inflightPromise = resolveConsumer(connectionChannelName, authToken).finally(
    () => {
      inflightPromise = null;
    },
  );

  return inflightPromise;
}

async function resolveConsumer(
  connectionChannelName: string,
  authToken: string | undefined,
): Promise<Consumer | null> {
  const token = await fetchAnycableToken(connectionChannelName, authToken);
  if (!token) {
    return null;
  }

  cachedToken = token;
  cachedChannelName = connectionChannelName;
  cachedAuthToken = authToken;

  if (cachedConsumer) {
    return cachedConsumer;
  }

  // URL function (re)evaluated on every connect attempt, so a token refreshed by
  // refreshAnycableToken is used by the next reconnect with no consumer recreation.
  cachedConsumer = createConsumer(buildConsumerUrl);
  setOnAnyDisconnected(refreshAnycableToken);
  return cachedConsumer;
}

function buildConsumerUrl(): string {
  return `${ANYCABLE_URL}?jid=${encodeURIComponent(cachedToken ?? '')}`;
}

/**
 * Re-fetch the JWT and update the cached token used to build the connection URL.
 * Invoked on disconnect so the connection monitor's next reconnect carries a fresh
 * token instead of an expired one. Concurrent callers share one in-flight request;
 * on failure the previous token is kept and the next disconnect retries.
 */
export async function refreshAnycableToken(): Promise<void> {
  if (!cachedChannelName) {
    return;
  }
  if (refreshPromise) {
    return refreshPromise;
  }

  const channelName = cachedChannelName;
  const authToken = cachedAuthToken;

  refreshPromise = fetchAnycableToken(channelName, authToken)
    .then(token => {
      if (token) {
        cachedToken = token;
      }
    })
    .catch(() => {
      // Keep the existing token; the next disconnect will try again.
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export function disconnectAnycable(): void {
  clearAllSubscriptions();
  setOnAnyDisconnected(null);
  if (cachedConsumer) {
    cachedConsumer.disconnect();
    cachedConsumer = null;
    cachedToken = null;
    cachedChannelName = null;
    cachedAuthToken = undefined;
  }
}
